import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { joinClass } from '../../lib/zoom-join';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { BookOpen, Calendar, Clock, Video, Check, Users, ChevronRight, LogOut, FileText, ArrowLeft, Lock, Loader2, RefreshCw } from 'lucide-react';
import { getBatchTypeLabel, getCourseTypeLabel, formatDate, formatTime, formatTimeLocal, formatLocalDate, getLocalDayMonth, toISTDateStr, getStatusColor, getInitials } from '../../lib/utils';
import { toast } from 'sonner';

export default function LearnerBatches() {
  const { user } = useAuth();
  const isElite = ['elite', 'gold', 'full'].includes(user?.learner_type);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalTab, setModalTab] = useState('upcoming');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  // V14.5 Part 17r — Recording state for the SessionDetail dialog. Previously
  // the setters were called without a corresponding useState declaration,
  // which threw a ReferenceError on click and prevented the modal from
  // opening at all.
  const [recordingData, setRecordingData] = useState(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  // V13.14.6 — read learner_join_buffer from /api/sessions/buffer-settings
  // (was hardcoded to 10 min; admin Settings was being ignored on this page).
  const [bufferSettings, setBufferSettings] = useState({ learner_join_buffer: 5 });

  useEffect(() => {
    api.get('/api/batches', { params: { limit: 50 } })
      .then(res => setBatches(res.data.batches))
      .finally(() => setLoading(false));
    api.get('/api/settings/buffers')
      .then(res => setBufferSettings(res.data))
      .catch(() => {});
  }, []);

  const openBatch = async (batch) => {
    // V14.5 — locked batches (unlock_rule not yet satisfied) cannot be opened.
    // Backend already hides their sessions, but we also short-circuit here so
    // the modal doesn't even attempt to load.
    if (batch?.access_state && batch.access_state.allowed === false) {
      return;
    }
    setSelectedBatch(batch);
    setModalTab('upcoming');
    setModalLoading(true);
    try {
      const [upRes, pastRes] = await Promise.all([
        api.get('/api/sessions', { params: { batch_id: batch.id, status: 'upcoming', limit: 50 } }),
        // V14.5 Part 18d — `status=past` returns completed + missed + canceled
        // sessions (not just `completed`). Backend also auto-filters to
        // sessions on/after the learner's LMS join date. Limit bumped to 200
        // because long-running MWF batches easily exceed 50 past sessions
        // over a year and we don't yet paginate this tab.
        api.get('/api/sessions', { params: { batch_id: batch.id, status: 'past', limit: 200 } }),
      ]);
      // Enrich upcoming with booking info
      const today = toISTDateStr(new Date());
      const availRes = await api.get('/api/sessions/available', { params: { date_from: today, date_to: '2026-12-31' } }).catch(() => ({ data: { sessions: [] } }));
      const availMap = {};
      for (const s of (availRes.data.sessions || [])) availMap[s.id] = s;
      
      const enriched = (upRes.data.sessions || []).map(s => ({
        ...s,
        ...(availMap[s.id] || {}),
      })).sort((a, b) => (a.scheduled_date + a.scheduled_time).localeCompare(b.scheduled_date + b.scheduled_time));
      setUpcoming(enriched);
      // V14.1 — past sessions: latest-first (descending) per Interpretation A
      const pastSorted = (pastRes.data.sessions || []).slice().sort(
        (a, b) => (b.scheduled_date + b.scheduled_time).localeCompare(a.scheduled_date + a.scheduled_time)
      );
      setPast(pastSorted);
    } catch { toast.error('Failed to load sessions'); }
    setModalLoading(false);
  };

  const handleBook = async (sessionId) => {
    try {
      const res = await api.post(`/api/sessions/${sessionId}/book`);
      toast.success(res.data.message || 'Booked!');
      if (selectedBatch) openBatch(selectedBatch);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to book'); }
  };

  const handleUnbook = async (sessionId) => {
    try {
      const res = await api.post(`/api/sessions/${sessionId}/unbook`);
      toast.info(res.data.message || 'Dropped out');
      setSelectedSession(null);
      setSessionDetail(null);
      if (selectedBatch) openBatch(selectedBatch);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // V14.5 Part 17q — Factored out so the Refresh button on the recording
  // "Processing..." state can re-poll without re-opening the modal.
  const loadRecordings = async (sessionId) => {
    setRecordingLoading(true);
    try {
      const res = await api.get(`/api/sessions/${sessionId}/recording`);
      setRecordingData(res.data);
    } catch (err) {
      // 403 (RECORDINGS_DISABLED) / 404 / other — leave recordingData null
      // so the UI shows the "not available" state instead of a misleading
      // "processing" message.
      setRecordingData(null);
    } finally {
      setRecordingLoading(false);
    }
  };

  const openSessionDetail = async (s) => {
    setSelectedBatch(null); // Close batch modal first
    setRecordingData(null);
    setRecordingLoading(false);
    setTimeout(async () => {
      setSelectedSession(s);
      let detail = null;
      try {
        const res = await api.get(`/api/sessions/${s.id}/detail`);
        detail = res.data;
        setSessionDetail(detail);
      } catch { setSessionDetail(null); }
      // Only completed sessions can have a recording — skip the /recording
      // call entirely for upcoming sessions to save a network round-trip.
      // V14.5 Part 18 — Also skip when the batch toggle disallows recording
      // sharing, so we don't show a misleading spinner / "preparing" state.
      if (s.status === 'completed' && detail?.share_recordings !== false) {
        loadRecordings(s.id);
      }
    }, 200);
  };

  return (
    <div className="space-y-6" data-testid="learner-batches">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">My Batches</h1>
        <p className="text-muted-foreground mt-1">{batches.length} courses enrolled</p>
      </div>

      {loading ? (
        <div className="space-y-4">{[1,2,3].map(i => <Card key={i} className="rounded-2xl animate-pulse"><CardContent className="h-24 pt-6" /></Card>)}</div>
      ) : batches.length === 0 ? (
        <Card className="rounded-2xl">
          <CardContent className="pt-6 text-center py-12">
            <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No courses enrolled yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {batches.map(b => {
            // V14.5 — Mirror locked-course UX (LearnTab.js CourseCard) for
            // sales-assigned batches that are gated by unlock_rule. Backend
            // returns `access_state: {allowed, lock_reason, lock_message}`
            // for learner role.
            const isLocked = b.access_state && b.access_state.allowed === false;
            const lockMessage = b.access_state?.lock_message;
            return (
              <Card
                key={b.id}
                className={`rounded-2xl transition-shadow ${isLocked ? 'opacity-60 cursor-not-allowed bg-slate-50' : 'hover:shadow-md cursor-pointer'}`}
                onClick={() => { if (!isLocked) openBatch(b); }}
                data-testid={`batch-card-${b.id}`}
                aria-disabled={isLocked || undefined}
              >
                <CardContent className="pt-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className={`text-base font-semibold ${isLocked ? 'text-slate-700' : ''}`}>{b.name}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{b.instructor_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{getBatchTypeLabel(b.batch_type)}</Badge>
                      <Badge variant="secondary">{getCourseTypeLabel(b.course_type)}</Badge>
                      {isLocked ? (
                        <Lock className="h-4 w-4 text-slate-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  {isLocked && lockMessage ? (
                    <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900 flex items-start gap-1.5"
                         data-testid={`batch-locked-msg-${b.id}`}>
                      <Lock className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>{lockMessage}</span>
                    </div>
                  ) : b.next_session ? (
                    <div className="p-3 rounded-lg bg-muted/40">
                      <p className="text-xs text-muted-foreground">Next Session</p>
                      <p className="text-sm font-medium">{formatLocalDate(b.next_session.scheduled_date, b.next_session.scheduled_time)} at {formatTimeLocal(b.next_session.scheduled_time, b.next_session.scheduled_date)}</p>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Batch Sessions Modal */}
      <Dialog open={!!selectedBatch} onOpenChange={(o) => { if (!o) setSelectedBatch(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">{selectedBatch?.name}</DialogTitle>
            <div className="flex gap-1.5 mt-1">
              <Badge variant="outline">{getBatchTypeLabel(selectedBatch?.batch_type)}</Badge>
              <Badge variant="secondary">{getCourseTypeLabel(selectedBatch?.course_type)}</Badge>
            </div>
          </DialogHeader>

          <Tabs value={modalTab} onValueChange={setModalTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upcoming">Upcoming ({upcoming.length})</TabsTrigger>
              <TabsTrigger value="past">Past ({past.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming" className="mt-3">
              {modalLoading ? (
                <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-16 bg-muted/40 rounded-xl animate-pulse" />)}</div>
              ) : upcoming.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No upcoming sessions</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {upcoming.map(s => (
                    <div key={s.id} className="flex items-center justify-between p-3 rounded-xl border cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => openSessionDetail(s)}>
                      <div className="flex items-center gap-3">
                        <div className="text-center bg-primary/10 rounded-lg p-2 min-w-[40px]">
                          {(() => {
                            const dm = getLocalDayMonth(s.scheduled_date, s.scheduled_time);
                            return (<>
                              <p className="text-[9px] text-primary uppercase">{dm.month}</p>
                              <p className="text-lg font-bold text-primary leading-tight">{dm.day}</p>
                            </>);
                          })()}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{formatTimeLocal(s.scheduled_time, s.scheduled_date)}</p>
                          <p className="text-xs text-muted-foreground">{s.duration_minutes}m · {s.instructor_name || ''}</p>
                          {selectedBatch?.batch_type !== 'one_on_one' && s.spots_left != null && (
                            <p className={`text-[10px] mt-0.5 ${s.is_full ? 'text-rose-600' : 'text-emerald-600'}`}>
                              {s.is_full ? `Full${s.waitlist_count ? ` · ${s.waitlist_count} waitlisted` : ''}` : `${s.spots_left} spots left`}
                            </p>
                          )}
                        </div>
                      </div>
                      <div onClick={e => e.stopPropagation()}>
                        {/* V13.14.6 — 1:1 batches: no booking concept. Show
                            "Join Now" if within join buffer, else just the
                            spot info. Group batches keep the Book/Cancel UX. */}
                        {selectedBatch?.batch_type === 'one_on_one' ? (() => {
                          // V14.5 Part 11 — Append IST offset so the Date is
                          // anchored to the actual moment, then compared with
                          // `new Date()` (UTC under the hood). Was missing
                          // before — parsed as local TZ → mins-until was off
                          // by 5.5h for IST learners and ~10h for CDT learners.
                          const sessionDt = new Date(`${s.scheduled_date}T${s.scheduled_time}:00+05:30`);
                          const minsUntil = (sessionDt - new Date()) / 60000;
                          const joinBufferMinutes = bufferSettings?.learner_join_buffer ?? 5;
                          if (minsUntil <= joinBufferMinutes && minsUntil > -120) {
                            return (
                              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                                      onClick={() => openSessionDetail(s)}
                                      data-testid={`one-on-one-join-${s.id}`}>
                                <Video className="h-3 w-3 mr-1" />Join Now
                              </Button>
                            );
                          }
                          return (
                            <Badge className="bg-primary/10 text-primary border-0 text-[10px]"
                                   data-testid={`one-on-one-scheduled-${s.id}`}>
                              <Check className="h-3 w-3 mr-0.5" />Scheduled
                            </Badge>
                          );
                        })() : s.is_booked_by_me ? (
                          <Badge className="bg-primary/10 text-primary border-0 text-[10px]"><Check className="h-3 w-3 mr-0.5" />Booked</Badge>
                        ) : s.is_waitlisted_by_me ? (
                          <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">Waitlisted</Badge>
                        ) : s.can_book !== false ? (
                          <Button size="sm" className="h-7 text-xs" onClick={() => handleBook(s.id)}>
                            {s.is_full ? 'Waitlist' : 'Book'}
                          </Button>
                        ) : s.has_booking_on_date ? (
                          <p className="text-[10px] text-muted-foreground">Booked another slot</p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="past" className="mt-3">
              {past.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No past sessions</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {past.map(s => (
                    <div key={s.id} className="flex items-center justify-between p-3 rounded-xl border cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => openSessionDetail(s)}>
                      <div className="flex items-center gap-3">
                        <div className="text-center bg-muted rounded-lg p-2 min-w-[40px]">
                          {(() => {
                            const dm = getLocalDayMonth(s.scheduled_date, s.scheduled_time);
                            return (<>
                              <p className="text-[9px] text-muted-foreground uppercase">{dm.month}</p>
                              <p className="text-lg font-bold text-muted-foreground leading-tight">{dm.day}</p>
                            </>);
                          })()}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{formatTimeLocal(s.scheduled_time, s.scheduled_date)}</p>
                          <p className="text-xs text-muted-foreground">{s.conducted_minutes ? `${Math.round(s.conducted_minutes)}m` : `${s.duration_minutes}m`} · {s.instructor_name || ''}</p>
                          <Badge variant="outline" className={`text-[9px] mt-0.5 ${getStatusColor(s.status)}`}>{s.status}</Badge>
                        </div>
                      </div>
                      {s.recording_url || (s.bunny_videos && s.bunny_videos.length > 0) ? (
                        <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                          <Video className="h-3 w-3 mr-0.5" />Recording
                        </Badge>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Session Detail Dialog */}
      <Dialog open={!!selectedSession} onOpenChange={(o) => { if (!o) { setSelectedSession(null); setSessionDetail(null); setRecordingData(null); setRecordingLoading(false); } }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedSession && (() => {
            const s = selectedSession;
            const d = sessionDetail || s;
            const isUpcoming = s.status === 'upcoming';
            const isCompleted = s.status === 'completed';
            // V13.14.6 — for 1:1 batches, the learner is auto-assigned (no booking).
            // Treat them as effectively booked so the Join button shows up.
            const isOneOnOne = selectedBatch?.batch_type === 'one_on_one';
            const isBooked = s.is_booked_by_me || isOneOnOne;
            const isWaitlisted = s.is_waitlisted_by_me;
            // V14.5 Part 11 — Append IST offset so the moment is correctly
            // computed regardless of the user's local TZ. Was missing — the
            // canJoin gate ran ~5.5h-10h skewed for non-IST learners.
            const sessionDt = new Date(`${s.scheduled_date}T${s.scheduled_time}:00+05:30`);
            const minutesUntil = (sessionDt - new Date()) / (1000 * 60);
            // V13.14.6 — read from settings instead of hardcoded 10 min
            const joinBufferMinutes = bufferSettings?.learner_join_buffer ?? 5;
            const canJoin = minutesUntil <= joinBufferMinutes && isBooked;

            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-lg">{d.batch_name || selectedBatch?.name || 'Session'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Session info */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30">
                    <div className="text-center bg-primary/10 rounded-lg p-2.5 min-w-[48px]">
                      {(() => {
                        const dm = getLocalDayMonth(s.scheduled_date, s.scheduled_time);
                        return (<>
                          <p className="text-[9px] text-primary uppercase">{dm.month}</p>
                          <p className="text-xl font-bold text-primary leading-tight">{dm.day}</p>
                        </>);
                      })()}
                    </div>
                    <div>
                      <p className="font-medium">{formatTimeLocal(s.scheduled_time, s.scheduled_date)}</p>
                      <p className="text-sm text-muted-foreground">{s.duration_minutes}m · {d.instructor_name || s.instructor_name || ''}</p>
                      <Badge variant="outline" className={`text-[10px] mt-1 ${getStatusColor(s.status)}`}>{s.status}</Badge>
                    </div>
                  </div>

                  {/* V14.7b — Credit consumption info for 1:1 completed/canceled sessions */}
                  {isOneOnOne && d.credit_info && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${d.credit_info.credit_used ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400'}`} data-testid="session-credit-info">
                      <span>{d.credit_info.credit_used ? '⬤' : '○'}</span>
                      <span>{d.credit_info.label}</span>
                    </div>
                  )}

                  {/* Join button for upcoming booked sessions */}
                  {isUpcoming && isBooked && (
                    <Button className="w-full" disabled={!canJoin} onClick={() => joinClass(s.id, s.zoom_join_url)} data-testid={`mybatches-join-${s.id}`}>
                      <Video className="h-4 w-4 mr-2" />{canJoin ? 'Join Class Now' : `Join available ${Math.max(0, Math.round(minutesUntil - joinBufferMinutes))} min before class`}
                    </Button>
                  )}

                  {/* Drop Out — Elite group bookings only. V65: a trial class is rescheduled or
                      cancelled by the team, and 1:1 cancellation goes through admin. */}
                  {isUpcoming && isBooked && !isOneOnOne && isElite && (
                    <Button variant="outline" className="w-full text-destructive border-destructive/30 hover:bg-destructive/5" onClick={() => handleUnbook(s.id)}>
                      <LogOut className="h-4 w-4 mr-2" />Drop Out of This Class
                    </Button>
                  )}

                  {/* Leave waitlist */}
                  {isUpcoming && isWaitlisted && (
                    <Button variant="outline" className="w-full" onClick={() => handleUnbook(s.id)}>
                      Leave Waitlist
                    </Button>
                  )}

                  {/* AI Summary for past sessions */}
                  {isCompleted && d.ai_summary && (
                    <>
                      <Separator />
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="h-4 w-4 text-primary" />
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Summary</p>
                          <Badge className="bg-primary/10 text-primary border-primary/20 text-[9px] px-1.5 py-0">SoulAI</Badge>
                        </div>
                        <p className="text-sm text-foreground/80 leading-relaxed">{d.ai_summary}</p>
                      </div>
                    </>
                  )}

                  {/* Topics Covered */}
                  {isCompleted && (d.topics_covered || []).length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5">Topics Covered</p>
                      <div className="flex flex-wrap gap-1.5">{d.topics_covered.map((t, i) => <Badge key={i} variant="outline" className="text-xs">{t}</Badge>)}</div>
                    </div>
                  )}

                  {/* Recording for past sessions
                      ────────────────────────────────────────────────────
                      V14.5 Part 17q — Reuses the SessionDetailModal pattern.
                      Fetches `/api/sessions/{id}/recording` (the admin-proven
                      signed-URL pipeline) and renders based on `source`:
                        • bunny             → inline player
                        • bunny_processing  → spinner + Refresh
                        • null / other      → "Not available yet" message
                      No more "Recording is being processed..." catch-all
                      false positive when a recording is actually ready. */}
                  {isCompleted && (() => {
                    // V14.5 Part 18 — When the batch has `share_recordings:false`,
                    // the learner is explicitly NOT entitled to see recordings.
                    // Render nothing (no header, no spinner, no "preparing" copy).
                    // This prevents the misleading "Recording is being prepared"
                    // message on batches whose admin has deliberately turned the
                    // share toggle off.
                    if (d.share_recordings === false) {
                      return null;
                    }
                    if (recordingLoading) {
                      return (
                        <>
                          <Separator />
                          <div data-testid="mybatches-recording-loading">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Recording</p>
                            <div className="flex items-center gap-2 py-4">
                              <Loader2 className="h-4 w-4 animate-spin text-primary" />
                              <p className="text-sm text-muted-foreground">Loading recording…</p>
                            </div>
                          </div>
                        </>
                      );
                    }
                    const bunnyVideos = recordingData?.bunny_videos || [];
                    // V14.5 Part 18 — Widen the filter so learners aren't locked
                    // out when Zoom only produced a non-speaker view (gallery /
                    // shared_screen / audio_only). Preference order:
                    //   1) speaker view (most natural for talking-head sessions)
                    //   2) ANY ready Bunny video with a signed embed URL.
                    // Before: strict `view_type === 'speaker'` → learners saw
                    // a permanent "Recording is being prepared" even though a
                    // perfectly valid gallery recording was sitting on Bunny.
                    const allReady = bunnyVideos.filter(v => v.status === 'ready' && v.embed_url);
                    const speakerReady = allReady.filter(v => v.view_type === 'speaker');
                    const readyVideos = speakerReady.length > 0 ? speakerReady : allReady;
                    // ── 1) Recording ready — play it inline ──
                    if (recordingData?.source === 'bunny' && readyVideos.length > 0) {
                      const v = readyVideos[0];
                      return (
                        <>
                          <Separator />
                          <div data-testid="mybatches-recording-ready">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Recording</p>
                            <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ paddingTop: '56.25%' }}>
                              <iframe
                                src={v.embed_url + (v.embed_url.includes('?') ? '&' : '?') + 'preload=true&autoplay=false&playsinline=true&responsive=true'}
                                loading="eager"
                                title="Session recording"
                                style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          </div>
                        </>
                      );
                    }
                    // ── 2) Still ingesting / encoding ──
                    if (recordingData?.source === 'bunny_processing') {
                      // V14.5 Part 18 — When Bunny encoding is still running
                      // (or stuck after a backend restart), we now also expose
                      // the underlying Zoom recording URL to learners (gated on
                      // batch `share_recordings`). Pre-fix, only admins got
                      // the Zoom fallback button — learners hit a dead end.
                      // The passcode is shown inline so the user doesn't have
                      // to hunt for it in Zoom's prompt.
                      const zoomUrl = recordingData?.recording_url;
                      const zoomPass = recordingData?.recording_passcode;
                      return (
                        <>
                          <Separator />
                          <div data-testid="mybatches-recording-processing">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Recording</p>
                            <div className="flex flex-col items-center gap-2 py-6 text-center">
                              <Loader2 className="h-6 w-6 animate-spin text-primary" />
                              <p className="text-sm">Recording is being processed…</p>
                              <p className="text-xs text-muted-foreground">
                                {recordingData?.ingest_kicked
                                  ? 'Just queued — the inline player will be ready in 1-3 minutes.'
                                  : 'This may take a few minutes.'}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Button variant="outline" size="sm" onClick={() => loadRecordings(s.id)} data-testid="mybatches-recording-refresh">
                                  <RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh
                                </Button>
                                {zoomUrl && (
                                  <Button
                                    asChild
                                    size="sm"
                                    variant="default"
                                    data-testid="mybatches-recording-zoom-fallback"
                                  >
                                    <a href={zoomUrl} target="_blank" rel="noopener noreferrer">
                                      <Video className="h-3.5 w-3.5 mr-1" />Watch on Zoom
                                    </a>
                                  </Button>
                                )}
                              </div>
                              {zoomUrl && zoomPass && (
                                <p className="text-xs text-muted-foreground mt-1" data-testid="mybatches-recording-zoom-passcode">
                                  Zoom passcode: <span className="font-mono font-medium text-foreground">{zoomPass}</span>
                                </p>
                              )}
                            </div>
                          </div>
                        </>
                      );
                    }
                    // ── 3) `share_recordings:false` (gated) OR no recording at all ──
                    //    We only show the section header at all if the session
                    //    actually had a recording. (`has_recording` is exposed
                    //    by `/sessions/{id}/detail`.) Otherwise stay silent.
                    if (d.has_recording || d.recording_url) {
                      return (
                        <>
                          <Separator />
                          <div data-testid="mybatches-recording-unavailable">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Recording</p>
                            <p className="text-sm text-muted-foreground py-4">
                              Recording is being prepared. Please check back in a few minutes.
                            </p>
                          </div>
                        </>
                      );
                    }
                    return null;
                  })()}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
